import { useEffect } from 'react';
import { Ico, burnerImgs, EDM_VIDEO } from '../shared';
import { PageHeader, SectionCTA } from '../Layout';

export default function ServicesPage() {
  useEffect(() => {
    document.title = 'Services | Ratnam Techo Inc. – Glass Burners, Wire EDM & Fabrication';
  }, []);

  return (
    <>
      <PageHeader
        title="Our Services"
        highlight="End-to-End Engineering"
        sub="From glass burner manufacturing to Wire EDM and full CNC fabrication — we do it all under one roof."
        crumb="Services"
      />

      <GlassBurnerService />
      <WireEDMService />
      <MachiningService />
      <PrototypingService />
      <RepairService />
      <MobileService />
      <SectionCTA heading="Need a Custom Solution?" sub="Every project is different. Tell us what you need and we'll build it right." />
    </>
  );
}

/* ── Glass Burner / Torch ── */
function GlassBurnerService() {
  const items = [
    'Custom torch design & engineering',
    'High-performance borosilicate torch builds',
    'Multi-flame burner configurations',
    'Prototype development for new concepts',
    'Fuel/air mixing optimization',
    'Pressure-tested for safety compliance',
  ];

  return (
    <section className="section" style={{ background: 'var(--white)' }}>
      <div className="section-inner">
        <div className="service-detail-grid">
          <div className="service-detail-imgs">
            <img src={burnerImgs[0]} alt="Glass burner manufacturing" className="sd-img-main" />
            <div className="sd-img-row">
              <img src={burnerImgs[1]} alt="Torch components" className="sd-img-thumb" />
              <img src={burnerImgs[2]} alt="Custom torch build" className="sd-img-thumb" />
            </div>
          </div>

          <div className="service-detail-content">
            <div className="service-detail-badge">
              <div className="service-detail-icon featured"><Ico name="flame" size={28} /></div>
              <span className="service-badge-label">Signature Service</span>
            </div>
            <h2 className="section-title">
              Glass Burner &<br /><span className="gold">Torch Solutions</span>
            </h2>
            <p className="section-sub" style={{ marginBottom: 24 }}>
              Our flagship service — precision-engineered glass burners and torches built
              for professional artists, glassblowers, and industrial applications. Every unit
              is crafted to exact specifications with safety and performance as top priorities.
            </p>
            <ul className="service-feature-list">
              {items.map((item, i) => (
                <li key={i}>
                  <span className="sfl-dot" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Wire EDM ── */
function WireEDMService() {
  const specs = [
    { label: 'Tolerance',   value: '±0.001"' },
    { label: 'Materials',   value: 'All conductive metals' },
    { label: 'Complexity',  value: 'Any 2D profile' },
    { label: 'Finish',      value: 'Mirror-class surface' },
  ];
  const items = [
    'Extreme dimensional accuracy on complex profiles',
    'No cutting force — zero material distortion',
    'Hardened steel, titanium, tungsten carbide',
    'Intricate internal cutouts and fine features',
    'Ideal for dies, punches, and precision tooling',
    'Fast setup, short lead times',
  ];

  return (
    <section className="wire-edm-section">
      <div className="wire-edm-inner">
        <div className="wire-edm-video-col">
          <div className="wire-edm-video-wrap">
            <video autoPlay muted loop playsInline>
              <source src={EDM_VIDEO} type="video/mp4" />
            </video>
            <div className="wire-edm-live-badge">
              <span className="wire-edm-live-dot" />
              Live Wire EDM Process
            </div>
          </div>
          <div className="edm-specs-row">
            {specs.map((s, i) => (
              <div className="edm-spec-card" key={i}>
                <div className="edm-spec-value">{s.value}</div>
                <div className="edm-spec-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="wire-edm-content-col">
          <div className="section-tag wire-edm-tag">
            <span className="section-tag-line" />
            Precision Technology
          </div>
          <h2 className="section-title wire-edm-title">
            Wire EDM<br /><span className="gold">Precision Cutting</span>
          </h2>
          <p className="section-sub wire-edm-sub" style={{ marginBottom: 24 }}>
            Wire Electrical Discharge Machining is the gold standard for cutting complex,
            high-precision profiles from any electrically conductive material — without
            applying any mechanical force that could distort the workpiece.
          </p>
          <ul className="wire-edm-features">
            {items.map((f, i) => (
              <li key={i}><span className="wire-edm-check">✓</span>{f}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ── CNC Machining ── */
function MachiningService() {
  const items = [
    'CNC turning and milling',
    'Manual lathe operations',
    'Tight-tolerance part production',
    'MIG/TIG welding & structural fabrication',
    'Stainless steel, aluminum, mild steel',
    'One-off parts and production runs',
  ];

  return (
    <section className="section" style={{ background: 'var(--off-white)' }}>
      <div className="section-inner">
        <div className="service-detail-grid service-detail-grid-rev">
          <div className="service-detail-content">
            <div className="service-detail-icon"><Ico name="settings" size={28} /></div>
            <h2 className="section-title">
              CNC Machining &<br /><span className="blue">Fabrication</span>
            </h2>
            <p className="section-sub" style={{ marginBottom: 24 }}>
              Our fully equipped shop handles everything from one-off prototype parts to
              short production runs. We machine to your print or work from scratch
              to deliver exactly what your project requires.
            </p>
            <ul className="service-feature-list">
              {items.map((item, i) => <li key={i}><span className="sfl-dot" />{item}</li>)}
            </ul>
          </div>

          <div className="service-detail-imgs">
            <img src={burnerImgs[3]} alt="CNC machining" className="sd-img-main" />
            <div className="sd-img-row">
              <img src={burnerImgs[4]} alt="Lathe operations" className="sd-img-thumb" />
              <img src={burnerImgs[5]} alt="Welding and fabrication" className="sd-img-thumb" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Prototyping ── */
function PrototypingService() {
  const steps = [
    { n: '01', title: 'Design Review',     desc: 'We analyze your concept, drawings, or 3D model and identify the best manufacturing approach.' },
    { n: '02', title: 'Material Selection', desc: 'We recommend the right material for your application based on performance, cost, and availability.' },
    { n: '03', title: 'Prototype Build',    desc: 'Using CNC machining, EDM, or fabrication — we produce your first prototype with care.' },
    { n: '04', title: 'Test & Refine',     desc: 'We test the prototype, incorporate your feedback, and iterate until it is production-ready.' },
  ];

  return (
    <section className="section" style={{ background: 'var(--white)' }}>
      <div className="section-inner">
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div className="section-tag" style={{ justifyContent: 'center' }}>
            <span className="section-tag-line" />
            Prototyping
          </div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Prototyping &<br /><span className="gold">Product Development</span>
          </h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            Turn your idea into a tangible, testable prototype with our end-to-end
            development process — from sketch to production-ready part.
          </p>
        </div>

        <div className="service-detail-imgs" style={{ marginBottom: 56 }}>
          <div className="sd-img-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {burnerImgs.slice(6, 9).map((src, i) => (
              <img key={i} src={src} alt={`Prototype stage ${i + 1}`} className="sd-img-thumb" style={{ height: 200 }} />
            ))}
          </div>
        </div>

        <div className="process-steps">
          {steps.map((s, i) => (
            <div className="process-step" key={i}>
              <div className="step-num">{s.n}</div>
              <div>
                <h3 className="step-title">{s.title}</h3>
                <p className="step-desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Repair & Maintenance ── */
function RepairService() {
  const items = [
    'Glass burner & torch repair',
    'Valve and regulator servicing',
    'Performance testing and calibration',
    'Part replacement and fabrication',
    'Preventive maintenance programs',
    'Emergency repair support',
  ];

  return (
    <section className="section" style={{ background: 'var(--off-white)' }}>
      <div className="section-inner">
        <div className="service-detail-grid">
          <div className="service-detail-imgs">
            <img src={burnerImgs[9]} alt="Equipment repair" className="sd-img-main" />
            <div className="sd-img-row">
              <img src={burnerImgs[10]} alt="Torch servicing" className="sd-img-thumb" />
              <div className="repair-stat-card">
                <div className="repair-stat-num">500+</div>
                <div className="repair-stat-text">Equipment units serviced</div>
              </div>
            </div>
          </div>

          <div className="service-detail-content">
            <div className="service-detail-icon"><Ico name="wrench" size={28} /></div>
            <h2 className="section-title">
              Repair &<br /><span className="blue">Maintenance</span>
            </h2>
            <p className="section-sub" style={{ marginBottom: 24 }}>
              Keep your equipment running at peak performance. Our experienced technicians
              diagnose, service, and repair glass burners, torches, and fabrication tooling
              quickly so your downtime stays minimal.
            </p>
            <ul className="service-feature-list">
              {items.map((item, i) => <li key={i}><span className="sfl-dot" />{item}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Mobile Service ── */
function MobileService() {
  return (
    <section className="section why" style={{ padding: '64px 24px' }}>
      <div className="why-bg" />
      <div className="section-inner" style={{ position: 'relative', zIndex: 2 }}>
        <div className="mobile-service-card">
          <div className="mobile-service-icon"><Ico name="truck" size={34} /></div>
          <div>
            <h3 className="mobile-service-title">Mobile On-Site Service</h3>
            <p className="mobile-service-desc">
              Can't bring your equipment to us? No problem. Our technicians come directly
              to your facility with the tools and expertise to service, repair, or install
              equipment on-site — anywhere in Ontario, minimizing your downtime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
